import React, { forwardRef, useEffect, useRef, useState, useImperativeHandle } from 'react'
import classnames from 'classnames'

import intlTelInput from 'intl-tel-input/build/js/intlTelInputWithUtils.js'

import { buildAriaProps, buildCss, buildDataProps, buildHtmlProps } from '../utilities/props'
import { globalProps } from '../utilities/globalProps'

import TextInput from '../pb_text_input/_text_input'
import { Callback } from '../types'
import { isEmpty } from '../utilities/object'

declare global {
  interface Window {
    intlTelInputGlobals: any
  }
}

type PhoneNumberInputProps = {
  aria?: { [key: string]: string },
  className?: string,
  dark?: boolean,
  data?: { [key: string]: string },
  disabled?: boolean,
  error?: string,
  hiddenInputs?: boolean,
  htmlOptions?: {[key: string]: string | number | boolean | (() => void)},
  id?: string,
  initialCountry?: string,
  isValid?: (valid: boolean) => void,
  label?: string,
  name?: string,
  onChange?: (e: React.FormEvent<HTMLInputElement>) => void,
  onValidate?: Callback<boolean, void>,
  onlyCountries: string[],
  excludeCountries: string[],
  preferredCountries?: string[],
  required?: boolean,
  requiredIndicator?: boolean,
  validationMessage?: string,
  value?: string,
  formatAsYouType?: boolean,
  strictMode?: boolean,
  countrySearch?: boolean,
  showPlaceholder?: boolean,
}

enum ValidationError {
  TooShort = 2,
  TooLong = 3,
  MissingAreaCode = 4,
  SomethingWentWrong = -99
}

const formatToGlobalCountryName = (countryName: string) => {
  return countryName.split("(")[0].trim()
}

const formatAllCountries = () => {
  const countryData = intlTelInput.getCountryData()
  for (let i = 0; i < countryData.length; i++) {
    const country = countryData[i]
    country.name = formatToGlobalCountryName(country.name)
  }
}

formatAllCountries()

const containOnlyNumbers = (value: string) => {
  // eslint-disable-next-line no-useless-escape
  return /^[()+\-\ .\d]*$/g.test(value)
}

const PhoneNumberInput = (props: PhoneNumberInputProps, ref?: React.Ref<unknown>) => {
  const {
    aria = {},
    className,
    dark = false,
    data = {},
    disabled = false,
    hiddenInputs = false,
    htmlOptions = {},
    id = "",
    initialCountry = "",
    isValid = () => {
      void 0
    },
    label = "",
    name = "",
    onChange = () => {
      void 0
    },
    onValidate = () => null,
    onlyCountries = [],
    excludeCountries = [],
    required = false,
    requiredIndicator = false,
    preferredCountries = [],
    validationMessage = "",
    value = "",
    formatAsYouType = false,
    strictMode = false,
    countrySearch = false,
    showPlaceholder = false,
  } = props

  const requiredFieldMessage = validationMessage || 'Missing phone number'

  const showPlaceholderRef = useRef(showPlaceholder)
  showPlaceholderRef.current = showPlaceholder

  const placeholderTemplateRef = useRef<string | null>(null)

  const ariaProps = buildAriaProps(aria)
  const dataProps = buildDataProps(data)
  const htmlProps = buildHtmlProps(htmlOptions)
  const classes = classnames(
    buildCss("pb_phone_number_input"),
    globalProps(props),
    className
  )

  const inputRef = useRef<HTMLInputElement | null>(null)
  const itiRef = useRef<any>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const hasBlurredRef = useRef<boolean>(false);
  const formSubmittedRef = useRef<boolean>(false);
  const [inputValue, setInputValue] = useState(value)
  const [error, setError] = useState(props.error || "")
  const [dropDownIsOpen, setDropDownIsOpen] = useState(false)
  const [selectedData, setSelectedData] = useState()
  const [hasTyped, setHasTyped] = useState(false)
  const [hasBlurred, setHasBlurred] = useState(false)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [hasStartedValidating, setHasStartedValidating] = useState(false)
  
  // Keep refs in sync with state for use in event listeners
  useEffect(() => {
    hasBlurredRef.current = hasBlurred
  }, [hasBlurred])
  
  useEffect(() => {
    formSubmittedRef.current = formSubmitted
  }, [formSubmitted])

  // Only sync initial error from props, not continuous updates
  // Once validation starts, internal validation takes over
  useEffect(() => {
    if (props.error && !hasStartedValidating) {
      setError(props.error)
      // If there's an initial error from props, mark as submitted so it shows
      if (props.error) {
        setFormSubmitted(true)
      }
    }
  }, [props.error, hasStartedValidating])

  // Function to update validation state on the wrapper element
  // Only applies when input is required
  const updateValidationState = (hasError: boolean) => {
    if (wrapperRef.current && required) {
      if (hasError) {
        wrapperRef.current.setAttribute('data-pb-phone-validation-error', 'true')
      } else {
        wrapperRef.current.removeAttribute('data-pb-phone-validation-error')
      }
    }
    
    // Set custom validity on the input element to prevent form submission
    if (inputRef.current) {
      if (hasError && error) {
        inputRef.current.setCustomValidity(error)
      } else {
        inputRef.current.setCustomValidity('')
      }
    }
  }

  // Determine which error to display
  // Show internal errors only after blur (hasBlurred) or on form submission (formSubmitted)
  const shouldShowInternalError = (hasBlurred || formSubmitted) && error
  const displayError = shouldShowInternalError ? error : ""

  useEffect(() => {
    const hasError = (error ?? '').length > 0
    if (hasError) {
      onValidate(false)
    } else {
      onValidate(true)
    }

    // Update validation state whenever error changes
    updateValidationState(hasError)
  }, [error, onValidate])

  const unformatNumber = (formattedNumber: any) => {
    return formattedNumber.replace(/\D/g, "")
  }

  const readInputPlaceholder = (element: HTMLInputElement) => {
    return element.getAttribute("placeholder") || element.placeholder || ""
  }

  const cachePlaceholderTemplate = (element: HTMLInputElement) => {
    const placeholder = readInputPlaceholder(element)
    if (placeholder) {
      placeholderTemplateRef.current = placeholder
    }
  }

  const hidePlaceholderIfFocusedAndEmpty = (element: HTMLInputElement) => {
    if (document.activeElement === element && !element.value) {
      element.setAttribute("placeholder", "")
      return true
    }
    return false
  }

  const hidePlaceholderIfEmpty = (element: HTMLInputElement) => {
    if (!element.value) {
      element.setAttribute("placeholder", "")
    }
  }

  const restorePlaceholderIfEmpty = (element: HTMLInputElement) => {
    if (!element.value && placeholderTemplateRef.current) {
      element.setAttribute("placeholder", placeholderTemplateRef.current)
    }
  }

  const showFormattedError = (reason = '') => {
    const countryName = itiRef.current.getSelectedCountryData().name
    const reasonText = reason.length > 0 ? ` (${reason})` : ''
    setError(`Invalid ${countryName} phone number${reasonText}`)
    return true
  }

  const validateTooLongNumber = (itiInit: any) => {
    if (!itiInit) return

    if (itiInit.getValidationError() === ValidationError.TooLong) {
      return showFormattedError('too long')
    } else {
      setError('')
    }
  }

  const validateTooShortNumber = (itiInit: any) => {
    if (!itiInit) return
    // If field is empty, don't show "too short" error
    if (!inputValue || inputValue.trim() === '') {
      setError('')
      return false
    }
    if (itiInit.getValidationError() === ValidationError.TooShort) {
      return showFormattedError('too short')
    } else {
      if (inputValue.length === 1) {
        return showFormattedError('too short')
      } else {
        setError('')
      }
    }
  }

  const validateOnlyNumbers = (itiInit: any) => {
    if (!itiInit) return
    if (inputValue && !containOnlyNumbers(inputValue)) {
      return showFormattedError('enter numbers only')
    }
  }

  const validateUnhandledError = (itiInit: any) => {
    if (!required || !itiInit) return
    if (itiInit.getValidationError() === ValidationError.SomethingWentWrong) {
      if (inputValue.length === 1) {
        return showFormattedError('too short')
      } else if (inputValue.length === 0) {
        setError(requiredFieldMessage)
        return true
      } else {
        return showFormattedError()
      }
    }
  }
  const validateMissingAreaCode = (itiInit: any) => {
    if (!itiInit) return
    if (itiInit.getValidationError() === ValidationError.MissingAreaCode) {
      showFormattedError('missing area code')
      return true
    }
  }

  const validateRepeatCountryCode = (itiInit: any) => {
    if (!itiInit) return
    const countryDialCode = itiRef.current.getSelectedCountryData().dialCode;
    if (unformatNumber(inputValue).startsWith(countryDialCode)) {
      return showFormattedError('repeat country code')
    }
  }

  // Validation for required empty fields
  const validateRequiredField = () => {
    if (required && (!inputValue || inputValue.trim() === '')) {
      setError(requiredFieldMessage)
      return true
    }
    return false
  }

  const validateErrors = () => {
    // Signal validation has started, so prop errors won't override internal validation
    if (!hasStartedValidating) {
      setHasStartedValidating(true)
    }

    // If field is empty, only show required field error if applicable
    if (!inputValue || inputValue.trim() === '') {
      if (validateRequiredField()) return
      // Clear any existing errors if field is empty and not required
      if (!required) {
        setError('')
      }
      return
    }

     // Only validate if field has been blurred or form has been submitted
     // Use refs here since state updates are async and we need current values
     if (!hasBlurredRef.current && !formSubmittedRef.current) return

    // Run validation checks
    if (itiRef.current) isValid(itiRef.current.isValidNumber())
    if (validateOnlyNumbers(itiRef.current)) return
    if (validateTooLongNumber(itiRef.current)) return
    if (validateTooShortNumber(itiRef.current)) return
    if (validateUnhandledError(itiRef.current)) return
    if (validateMissingAreaCode(itiRef.current)) return
    if (validateRepeatCountryCode(itiRef.current)) return
  }

  // Add listener for form validation to track when validation should be shown
  useEffect(() => {
    const handleInvalid = (event: Event) => {
      const target = event.target as HTMLInputElement
      const phoneNumberContainer = target.closest('.pb_phone_number_input')

      if (phoneNumberContainer && phoneNumberContainer === wrapperRef.current) {
        const invalidInputName = target.name || target.getAttribute('name')
        if (invalidInputName === name) {
          formSubmittedRef.current = true
          setFormSubmitted(true)
          // Trigger validation when form is submitted
          validateErrors()
        }
      }
    }

    document.addEventListener('invalid', handleInvalid, true)

    return () => {
      document.removeEventListener('invalid', handleInvalid, true)
    }
  }, [name, inputValue])

  /*
    useImperativeHandle exposes the kit's input element to a parent component via a ref.
    See the Playbook docs for use cases.
    Read: https://react.dev/reference/react/useImperativeHandle
  */
  useImperativeHandle(ref, () => {
    return {
      clearField() {
        setInputValue("")
        setError("")
        setHasTyped(false)
        hasBlurredRef.current = false
        setHasBlurred(false)
        formSubmittedRef.current = false
        setFormSubmitted(false)
        setHasStartedValidating(false)
        // Only clear validation state if field was required
        if (required) {
          updateValidationState(false)
        }
      },
      inputNode() {
        return inputRef.current
      },
      // Expose validation method for React Hook Form
      validate() {
        // Run validation and return error message or true
        const isEmpty = !inputValue || inputValue.trim() === ''

        if (required && isEmpty) {
          setError(requiredFieldMessage)
          formSubmittedRef.current = true
          setFormSubmitted(true)
          return requiredFieldMessage
        }

        if (isEmpty) {
          // Show missing phone number error
          setError(requiredFieldMessage)
          setHasTyped(true)
          // Only return error for React Hook Form if field is required
          return required ? requiredFieldMessage : true
        }

        if (!itiRef.current) {
          return true
        }

        // Check for repeat country code first
        const countryDialCode = itiRef.current.getSelectedCountryData().dialCode;
        if (unformatNumber(inputValue).startsWith(countryDialCode)) {
          const countryName = itiRef.current.getSelectedCountryData().name
          const errorMessage = `Invalid ${countryName} phone number (repeat country code)`
          setError(errorMessage)
          setFormSubmitted(true)
          setHasTyped(true)
          return errorMessage
        }

        // Check if it only contains valid characters
        if (!containOnlyNumbers(inputValue)) {
          const countryName = itiRef.current.getSelectedCountryData().name
          const errorMessage = `Invalid ${countryName} phone number (enter numbers only)`
          setError(errorMessage)
          setFormSubmitted(true)
          setHasTyped(true)
          return errorMessage
        }

        // Check if valid number
        if (!itiRef.current.isValidNumber()) {
          const countryName = itiRef.current.getSelectedCountryData().name
          const validationError = itiRef.current.getValidationError()
          let errorMessage = ''

          if (validationError === ValidationError.TooShort) {
            errorMessage = `Invalid ${countryName} phone number (too short)`
          } else if (validationError === ValidationError.TooLong) {
            errorMessage = `Invalid ${countryName} phone number (too long)`
          } else if (validationError === ValidationError.MissingAreaCode) {
            errorMessage = `Invalid ${countryName} phone number (missing area code)`
          } else {
            errorMessage = `Invalid ${countryName} phone number`
          }

          // Set the error state so the validation attribute gets added
          setError(errorMessage)
          formSubmittedRef.current = true
          setFormSubmitted(true)
          setHasTyped(true)

          return errorMessage
        }

        // Clear error if valid
        setError('')
        return true
      }
    }
  })

  const getCurrentSelectedData = (itiInit: any, inputValue: string) => {
    return { ...itiInit.getSelectedCountryData(), number: inputValue }
  }

  const handleOnChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    if (!hasTyped) setHasTyped(true)
    setInputValue(evt.target.value)

    // Reset form submitted state when user types
    if (formSubmitted) {
      formSubmittedRef.current = false
      setFormSubmitted(false)
    }

    let phoneNumberData

    // Handle formatAsYouType with input event
    if (formatAsYouType) {
      const formattedPhoneNumberData = getCurrentSelectedData(itiRef.current, evt.target.value)
      phoneNumberData = {...formattedPhoneNumberData, number: unformatNumber(formattedPhoneNumberData.number)}
    } else {
      phoneNumberData = getCurrentSelectedData(itiRef.current, evt.target.value)
    }

    setSelectedData(phoneNumberData)
    onChange(phoneNumberData)
    
    // Don't call isValid callback on change - only on blur or form submission
    // This prevents triggering validation while typing
    // Use refs to get current values in case this is called from event listener
    if (hasBlurredRef.current || formSubmittedRef.current) {
      isValid(itiRef.current.isValidNumber())
    }

    // Don't validate on change - only validate on blur or form submission
  }

  // Separating Concerns as React Docs Recommend
  // This also Fixes things for our react_component rendering on the Rails Side
  useEffect(formatAllCountries, [])
  // If an initial country is not specified, the "globe" icon will show
  // Always set a country
  const fallbackCountry =
    preferredCountries.length > 0 ? preferredCountries[0] :
      onlyCountries.length > 0 ? onlyCountries.sort()[0] :
      excludeCountries.length > 0 ? excludeCountries.sort()[0] :
        "af";

  useEffect(() => {
    const telInputInit = intlTelInput(inputRef.current, {
      separateDialCode: true,
      countryOrder: preferredCountries,
      allowDropdown: !disabled,
      autoInsertDialCode: false,
      autoPlaceholder: showPlaceholderRef.current ? "polite" : "off",
      initialCountry: initialCountry || fallbackCountry,
      onlyCountries,
      excludeCountries,
      countrySearch: countrySearch,
      fixDropdownWidth: false,
      formatAsYouType: formatAsYouType,
      strictMode: strictMode,
      hiddenInput: hiddenInputs ? () => ({
        phone: `${name}_full`,
        country: `${name}_country_code`,
      }) : null,
    })

    itiRef.current = telInputInit;

    if (inputRef.current) {
      inputRef.current.addEventListener("countrychange", (evt: Event) => {
        const phoneNumberData = getCurrentSelectedData(telInputInit, (evt.target as HTMLInputElement).value)
        setSelectedData(phoneNumberData)
        onChange(phoneNumberData)
        validateErrors()

        if (showPlaceholderRef.current) {
          const syncPlaceholderState = () => {
            const el = inputRef.current
            if (!el) return

            cachePlaceholderTemplate(el)
            if (hidePlaceholderIfFocusedAndEmpty(el)) return
            restorePlaceholderIfEmpty(el)
          }

          // Run immediately, then once on the next tick in case intl-tel-input
          // updates the placeholder asynchronously after countrychange.
          syncPlaceholderState()
          setTimeout(syncPlaceholderState, 0)
        }
      })

      inputRef.current.addEventListener("open:countrydropdown", () => setDropDownIsOpen(true))
      inputRef.current.addEventListener("close:countrydropdown", () => setDropDownIsOpen(false))

    // Handle formatAsYouType with input event
    if (formatAsYouType) {
      inputRef.current.addEventListener("input", (evt: Event) => {
          const target = evt.target as HTMLInputElement
          const formattedValue = target.value

          // Update internal state
          setInputValue(formattedValue)
          setHasTyped(true)

          // Get phone number data with unformatted number
          const formattedPhoneNumberData = getCurrentSelectedData(telInputInit, formattedValue)
          const phoneNumberData = {...formattedPhoneNumberData, number: unformatNumber(formattedPhoneNumberData.number)}

          setSelectedData(phoneNumberData)
          onChange(phoneNumberData)
          
          // Don't call isValid callback on change - only on blur or form submission
          // Use refs to check current blur state in the event listener (closure issue)
          if (hasBlurredRef.current || formSubmittedRef.current) {
            isValid(telInputInit.isValidNumber())
          }
        })
      }
    }
  }, [])
  let textInputProps: {[key: string]: any} = {
    className: dropDownIsOpen ? 'dropdown_open' : '',
    dark,
    "data-phone-number": JSON.stringify(selectedData),
    disabled,
    error: displayError || props.error || "",
    type: 'tel',
    id,
    label,
    name,
    onFocus: () => {
      if (!showPlaceholder) return
      const el = inputRef.current
      if (!el || el.value) return
      cachePlaceholderTemplate(el)
      hidePlaceholderIfEmpty(el)
    },
    onBlur: () => {
      if (showPlaceholder) {
        const el = inputRef.current
        if (el && !el.value) {
          restorePlaceholderIfEmpty(el)
        }
      }
      hasBlurredRef.current = true
      setHasBlurred(true)
      validateErrors()
    },
    onChange: formatAsYouType ? undefined : handleOnChange,
    requiredIndicator,
    value: inputValue
  }

  let wrapperProps: Record<string, unknown> = {
    className: classes,
    ref: wrapperRef
  }

  if (!isEmpty(aria)) textInputProps = {...textInputProps, ...ariaProps}
  if (!isEmpty(data)) wrapperProps = {...wrapperProps, ...dataProps}
  if (required) textInputProps.required = true

  return (
    <div
        {...wrapperProps}
        {...htmlProps}
    >
      <TextInput
          ref={inputNode => {
            if (ref) {
              if (typeof ref === 'function') {
                ref(inputNode)
              } else {
                (ref as React.MutableRefObject<HTMLInputElement | null>).current = inputNode
              }
            }
            inputRef.current = inputNode
          }}
          {...textInputProps}
      />
    </div>
  )
}

export default forwardRef(PhoneNumberInput)
